import React, { useCallback, useEffect, useRef, useState } from "react"

import { Search as SearchIcon } from '@mui/icons-material'
import {
  Box,
  CircularProgress,
  ClickAwayListener,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material'
import { useNavigate } from "react-router-dom"

import { ordersService, usersService, type Order, type User } from "../api"

const DEBOUNCE_MS = 300
const MIN_SEARCH_LENGTH = 3
const MAX_RESULTS_PER_CATEGORY = 5

type SearchResult =
  | { type: 'user'; data: User }
  | { type: 'order'; data: Order }

type SearchSource = 'users' | 'orders'

const describeFailure = (failedSources: SearchSource[]): string => {
  return failedSources.length > 1
    ? 'Search failed. Click to retry'
    : `Could not load ${failedSources.join('')}. Click to retry`
}

const highlightMatch = (text: string, query: string) => {
  if (!query || query.length < MIN_SEARCH_LENGTH) return text

  const lowerText = text.toLowerCase()
  const lowerQuery = query.toLowerCase()
  const startIndex = lowerText.indexOf(lowerQuery)

  if (startIndex === -1) return text

  const before = text.slice(0, startIndex)
  const match = text.slice(startIndex, startIndex + query.length)
  const after = text.slice(startIndex + query.length)

  return (
    <>
      {before}
      <Box component="span" sx={{ fontWeight: 700, color: 'primary.main' }}>{match}</Box>
      {after}
    </>
  )
}

export function GlobalSearch() {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState(-1)
  const [hasSearched, setHasSearched] = useState(false)
  const [failedSources, setFailedSources] = useState<SearchSource[]>([])
  const anchorRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const requestIdRef = useRef(0)
  const abortControllerRef = useRef<AbortController | null>(null)
  const navigate = useNavigate()
  const trimmedQuery = query.trim()

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        inputRef.current?.focus()
        inputRef.current?.select()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const resetSearchState = useCallback(() => {
    requestIdRef.current += 1
    abortControllerRef.current?.abort()
    setResults([])
    setHasSearched(false)
    setFailedSources([])
    setIsLoading(false)
  }, [])

  const search = useCallback(async (searchQuery: string) => {
    requestIdRef.current += 1
    const currentRequestId = requestIdRef.current

    abortControllerRef.current?.abort()
    const abortController = new AbortController()
    abortControllerRef.current = abortController

    setIsLoading(true)
    const [usersResult, ordersResult] = await Promise.allSettled([
      usersService.getUsers({
        search: searchQuery,
        size: MAX_RESULTS_PER_CATEGORY,
      }, { signal: abortController.signal }),
      ordersService.getOrders({
        search: searchQuery,
        limit: MAX_RESULTS_PER_CATEGORY,
      }, { signal: abortController.signal }),
    ])

    if (currentRequestId !== requestIdRef.current) return

    const users = usersResult.status === 'fulfilled' ? usersResult.value.users : []
    const orders = ordersResult.status === 'fulfilled' ? ordersResult.value.items : []

    const combined: SearchResult[] = [
      ...users.map((data): SearchResult => ({ type: 'user', data })),
      ...orders.map((data): SearchResult => ({ type: 'order', data })),
    ]

    const failedUsers: SearchSource[] = usersResult.status === 'rejected' ? ['users'] : []
    const failedOrders: SearchSource[] = ordersResult.status === 'rejected' ? ['orders'] : []

    setResults(combined)
    setFailedSources([...failedUsers, ...failedOrders])
    setHighlightedIndex(-1)
    setHasSearched(true)
    setIsOpen(true)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    return () => {
      requestIdRef.current += 1
      abortControllerRef.current?.abort()
    }
  }, [])

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
    }

    if (trimmedQuery.length < MIN_SEARCH_LENGTH) {
      resetSearchState()
      setIsOpen(trimmedQuery.length > 0)
      return
    }

    debounceRef.current = setTimeout(() => {
      search(trimmedQuery)
    }, DEBOUNCE_MS)

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
      }
    }
  }, [trimmedQuery, search, resetSearchState])

  const handleSelect = (result: SearchResult) => {
    resetSearchState()
    setIsOpen(false)
    setQuery("")

    if (result.type === 'user') {
      navigate(`/users/${result.data.id}`)
    } else {
      navigate(`/orders/${result.data.id}`)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex(prev =>
        prev < results.length - 1 ? prev + 1 : 0
      )
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex(prev =>
        prev > 0 ? prev - 1 : results.length - 1
      )
    }

    if (e.key === 'Enter' && highlightedIndex >= 0) {
      e.preventDefault()
      const selected = results[highlightedIndex]
      if (selected) handleSelect(selected)
    }

    if (e.key === 'Escape') {
      setIsOpen(false)
      inputRef.current?.blur()
    }
  }

  const userResults = results.filter((r): r is SearchResult & { type: 'user' } => r.type === 'user')
  const orderResults = results.filter((r): r is SearchResult & { type: 'order' } => r.type === 'order')

  const getGlobalIndex = (type: 'user' | 'order', localIndex: number): number => {
    if (type === 'user') return localIndex
    return userResults.length + localIndex
  }

  const renderUserResult = (result: SearchResult & { type: 'user' }, localIndex: number) => {
    const user = result.data
    const globalIndex = getGlobalIndex('user', localIndex)
    const displayName = [user.firstName, user.lastName].filter(Boolean).join(' ')
    const secondaryParts: string[] = []
    if (displayName) secondaryParts.push(displayName)
    if (user.countryCode) secondaryParts.push(user.countryCode)
    if (user.externalProfileId) secondaryParts.push(user.externalProfileId)
    const secondaryText = secondaryParts.join(' · ')

    return (
      <Box
        key={`user-${user.id}`}
        onClick={() => handleSelect(result)}
        sx={{
          px: 2,
          py: 1,
          cursor: 'pointer',
          bgcolor: globalIndex === highlightedIndex ? 'action.hover' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Typography variant="body2" fontWeight={500}>
          {highlightMatch(user.email, trimmedQuery)}
        </Typography>
        {secondaryText && (
          <Typography variant="caption" color="text.secondary">
            {highlightMatch(secondaryText, trimmedQuery)}
          </Typography>
        )}
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.68rem' }}>
          {highlightMatch(user.id, trimmedQuery)}
        </Typography>
      </Box>
    )
  }

  const renderOrderResult = (result: SearchResult & { type: 'order' }, localIndex: number) => {
    const order = result.data
    const globalIndex = getGlobalIndex('order', localIndex)
    const secondaryParts: string[] = []
    if (order.user?.email) secondaryParts.push(order.user.email)
    if (order.user?.firstName || order.user?.lastName) {
      secondaryParts.push([order.user.firstName, order.user.lastName].filter(Boolean).join(' '))
    }
    const secondaryText = secondaryParts.join(' · ')
    const metaParts: string[] = []
    if (order.packageId) metaParts.push(order.packageId)
    if (order.junctionOrderId) metaParts.push(order.junctionOrderId)
    const metaText = metaParts.join(' · ')

    return (
      <Box
        key={`order-${order.id}`}
        onClick={() => handleSelect(result)}
        sx={{
          px: 2,
          py: 1,
          cursor: 'pointer',
          bgcolor: globalIndex === highlightedIndex ? 'action.hover' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
            {highlightMatch(order.id, trimmedQuery)}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              px: 0.75,
              py: 0.125,
              borderRadius: 0.5,
              bgcolor: 'grey.100',
              color: 'text.secondary',
              fontSize: '0.7rem',
              flexShrink: 0,
            }}
          >
            {order.status}
          </Typography>
        </Box>
        {secondaryText && (
          <Typography variant="caption" color="text.secondary">
            {highlightMatch(secondaryText, trimmedQuery)}
          </Typography>
        )}
        {metaText && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontSize: '0.68rem' }}>
            {highlightMatch(metaText, trimmedQuery)}
          </Typography>
        )}
      </Box>
    )
  }

  const showMinLengthHint = trimmedQuery.length > 0 && trimmedQuery.length < MIN_SEARCH_LENGTH
  const showError = hasSearched && failedSources.length > 0 && !isLoading
  const showNoResults = hasSearched && results.length === 0 && !isLoading && failedSources.length === 0

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <Box ref={anchorRef} sx={{ position: 'relative', width: { xs: 200, sm: 320 } }}>
        <TextField
          size="small"
          placeholder="Search users, orders...  ⌘K"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setHighlightedIndex(-1)
          }}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (results.length > 0 || showNoResults || showError || showMinLengthHint) setIsOpen(true)
          }}
          inputRef={inputRef}
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
              endAdornment: isLoading ? (
                <InputAdornment position="end">
                  <CircularProgress size={16} />
                </InputAdornment>
              ) : null,
              sx: {
                bgcolor: 'grey.100',
                borderRadius: 1,
                '& fieldset': { border: 'none' },
                fontSize: '0.875rem',
              },
            },
          }}
        />

        <Popper
          open={isOpen}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          sx={{ zIndex: 1300, width: anchorRef.current?.offsetWidth }}
        >
          <Paper
            elevation={8}
            sx={{
              mt: 0.5,
              maxHeight: 400,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {showMinLengthHint && (
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Type at least {MIN_SEARCH_LENGTH} characters
                </Typography>
              </Box>
            )}

            {showError && (
              <Box
                onClick={() => search(trimmedQuery)}
                sx={{ px: 2, py: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
              >
                <Typography variant="body2" color="error" textAlign="center">
                  {describeFailure(failedSources)}
                </Typography>
              </Box>
            )}

            {showNoResults && (
              <Box sx={{ px: 2, py: 2 }}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  No results for &ldquo;{trimmedQuery}&rdquo;
                </Typography>
              </Box>
            )}

            {userResults.length > 0 && (
              <>
                <Typography
                  variant="overline"
                  sx={{ px: 2, pt: 1, pb: 0.5, display: 'block', color: 'text.secondary', lineHeight: 1.5 }}
                >
                  Users
                </Typography>
                {userResults.map((result, index) => renderUserResult(result, index))}
              </>
            )}

            {orderResults.length > 0 && (
              <>
                <Typography
                  variant="overline"
                  sx={{
                    px: 2,
                    pt: userResults.length > 0 ? 1.5 : 1,
                    pb: 0.5,
                    display: 'block',
                    color: 'text.secondary',
                    lineHeight: 1.5,
                    borderTop: userResults.length > 0 ? '1px solid' : 'none',
                    borderTopColor: 'divider',
                  }}
                >
                  Orders
                </Typography>
                {orderResults.map((result, index) => renderOrderResult(result, index))}
              </>
            )}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  )
}
